import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, finalize, map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Client } from "../domain/client";
import { Contract } from "../domain/contract";
import { User } from "../domain/user";
import { CoreData } from "../tools/core-data/core-data";
import { LoadingTask } from "../tools/ui/loading-task";
import { BaseService } from "./base-service";
import { AuthentificationDto } from "./DTO/authentification-dto";
import * as sha512 from 'js-sha512';
import { AlternateContactDto } from "./DTO/alternate-contact.dto";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Permissions } from "../domain/user-profile/permissions";

@Injectable({
    providedIn: 'root',
})
export class UserService extends BaseService {


    private serviceUrl = `${environment.serviceBaseUrl}api/user`;

    constructor(public http: HttpClient,
        protected coreData: CoreData,
        protected snackBare: MatSnackBar,
        private loadingTask: LoadingTask,
        protected router: Router) {
        super(router, coreData, snackBare);
    }

    tryCredential(user: User) {
        const dto: AuthentificationDto = new AuthentificationDto();
        dto.ClientKey = this.getFingerPrintGuid();
        dto.Email = user.Email;
        dto.Password = sha512.sha512(user.Password);
        const body = JSON.stringify(dto);
        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/Authentication`, body, this.generateHeader()).pipe(
            catchError(this.handleError<any>('tryCredential')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: string) => {
                return response;
            }));
    }

    askForResetPassword(email: string) {

        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/${encodeURI(email)}/passwordChangeRequest`, null, this.generateHeader()).pipe(
            catchError(this.handleError<any>('askForResetPassword')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    askForAccess(User: User) {
        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/NEWCUSTOMER`, User, this.generateHeader()).pipe(
            catchError(this.handleError<any>('askForAccess')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    resetPassword(email: string, key: string, password: string) {
        const dto = {
            newPassword: sha512.sha512(password),
            email: email,
            key: key
        };

        const body = JSON.stringify(dto);

        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/passwordReset`, body, this.generateHeader()).pipe(
            catchError(this.handleError<any>('resetPassword')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    // !==DEPRECATED ???
    getAllELIMSUserList(text: string) { // To search a new user to be added in our DB
        let params = new HttpParams();
        if (text) {
            params = params.append('filter', `${text}`);
        }
        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/elims`, this.generateHeaderWithParameters(params)).pipe(
            catchError(this.handleError<any>('getAllELIMSUserList')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: User) => {
                return User.mapFromJSON(response);
            }));
    }
    /// !==

    getAlternateContactListByClient(clientCode: string) {

        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/alternateContact/${clientCode}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getAlternateContact')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: User[]) => {
                return response.map((item: User) => {
                    return User.mapFromJSON(item);
                });
            }));
    }

    getSiteContactList(strategyId: number) {

        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/siteContact/${strategyId}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getSiteContact')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: User[]) => {
                return response.map((item: User) => {
                    return User.mapFromJSON(item);
                });
            }));
    }

    getMyUser() {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/my`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getMyUser')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: User) => {
                return User.mapFromJSON(response);
            }));
    }

    addAlternateContact(user: User, strategyId: number) {
        const dto: AlternateContactDto = new AlternateContactDto();
        dto.Email = user.Email;
        dto.LastName = user.LastName;
        dto.FirstName = user.FirstName;
        dto.Phone = user.Phone;
        dto.StrategyId = strategyId;
        const body = JSON.stringify(dto);
        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/alternateContact`, body, this.generateHeader()).pipe(
            catchError(this.handleError<any>('addAlternateContact')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: string) => {
                return response;
            }));
    }

    getRolePermissions() {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/Role`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getRoles')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Permissions[]) => {
                return response.map((item: Permissions) => {
                    return Permissions.mapFromJSON(item);
                });
            }));
    }
}